import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { BadgeSize, BadgeType, BadgeVariant } from './types'

@safeCustomElement('mdc-badge')
export class MdcBadge extends LitElement {
  @property({ type: String, reflect: true }) size: BadgeSize = 'md'
  @property({ type: String }) variant: BadgeVariant = 'primary'
  @property({ type: String }) type: BadgeType = 'static'
  @property({ type: Boolean, attribute: 'is-clickable' }) isClickable = false
  @property({ type: String }) href = ''
  @property({ type: Boolean }) disabled = false

  static styles = css`
		:host {
			font-family: var(--typography-font-family-sans);
			/* Badge Design Tokens */
			--mdc-badge-height: 1.5rem;
			--mdc-badge-font-family: var(--typography-font-family-sans);
			--mdc-badge-font-size: var(--text-062-and-half, 0.625rem);
			--mdc-badge-font-size-desktop: var(--text-075, 0.75rem);
			--mdc-badge-font-weight: 450;
			--mdc-badge-line-height: 1.3;
			--mdc-badge-border-radius: var(--radius-xs, 0.25rem);
			--mdc-badge-padding-vertical: var(--spacing-025, 0.25rem);
			--mdc-badge-padding-horizontal: var(--spacing-050, 0.5rem);
			--mdc-badge-gap: 0.625rem;
			--mdc-badge-transition-duration: 0.2s;
			--mdc-badge-transition-timing: ease;

			/* Badge Primary Variant */
			--mdc-badge-primary-color: var(--color-functional-gray-black, #000000);
			--mdc-badge-primary-background: var(--color-functional-gray-25, #fafafa);
			--mdc-badge-primary-background-hover: var(--color-functional-gray-50, #f5f5f5);
			--mdc-badge-primary-color-disabled: var(--color-functional-gray-300, #d4d4d4);
			--mdc-badge-primary-background-disabled: var(--color-functional-gray-50, #f5f5f5);

			/* Badge Inverse Variant - Swapped colors from primary */
			--mdc-badge-inverse-color: var(--color-functional-gray-25, #fafafa);
			--mdc-badge-inverse-background: var(--color-functional-gray-black, #000000);
			--mdc-badge-inverse-border: var(--color-functional-gray-400, #a3a3a3);
			--mdc-badge-inverse-background-hover: var(--color-functional-gray-800, #262626);
			--mdc-badge-inverse-border-hover: var(--color-functional-gray-200, #e5e5e5);
			--mdc-badge-inverse-color-disabled: var(--color-functional-gray-300, #d4d4d4);
			--mdc-badge-inverse-background-disabled: var(--color-functional-gray-800, #262626);
			--mdc-badge-inverse-border-disabled: var(--color-functional-gray-400, #a3a3a3);

			/* Badge Green Variant */
			--mdc-badge-green-color: var(--color-functional-green-800, #06492B);
			--mdc-badge-green-background: var(--color-functional-green-50, #E8F5ED);
			--mdc-badge-green-background-hover: var(--color-functional-green-100, #C3E6D0);
			--mdc-badge-green-color-disabled: var(--color-functional-green-300, #3DB87A);
			--mdc-badge-green-background-disabled: var(--color-functional-green-50, #E8F5ED);

			/* Badge Red Variant */
			--mdc-badge-red-color: var(--color-functional-red-800, #74181D);
			--mdc-badge-red-background: var(--color-functional-red-50, #FAEEED);
			--mdc-badge-red-background-hover: var(--color-functional-red-100, #F5CBC8);
			--mdc-badge-red-color-disabled: var(--color-functional-red-300, #D0615E);
			--mdc-badge-red-background-disabled: var(--color-functional-red-50, #FAEEED);

			/* Badge Blue Variant */
			--mdc-badge-blue-color: var(--color-functional-blue-800, #053D69);
			--mdc-badge-blue-background: var(--color-functional-blue-50, #E4F2FF);
			--mdc-badge-blue-background-hover: var(--color-functional-blue-100, #B2D9FF);
			--mdc-badge-blue-color-disabled: var(--color-functional-blue-300, #0095F6);
			--mdc-badge-blue-background-disabled: var(--color-functional-blue-50, #E4F2FF);
		}

		:host {
			display: inline-flex;
		}

		a.mdc-badge,
		button.mdc-badge {
			text-decoration: none;
		}

		button.mdc-badge {
			margin: 0;
			border: none;
			background: none;
		}

		a.mdc-badge:not(:disabled):not([aria-disabled='true']),
		button.mdc-badge:not(:disabled):not([aria-disabled='true']) {
			cursor: pointer;
		}

		a.mdc-badge:not(:disabled):not([aria-disabled='true']):focus-visible,
		button.mdc-badge:not(:disabled):not([aria-disabled='true']):focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		a.mdc-badge:disabled,
		a.mdc-badge[aria-disabled='true'],
		button.mdc-badge:disabled,
		button.mdc-badge[aria-disabled='true'] {
			cursor: not-allowed;
		}

		.mdc-badge {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			height: var(--mdc-badge-height);
			font-family: var(--mdc-badge-font-family);
			font-size: var(--mdc-badge-font-size);
			font-weight: var(--mdc-badge-font-weight);
			line-height: var(--mdc-badge-line-height);
			border-radius: var(--mdc-badge-border-radius);
			box-sizing: border-box;
			padding: var(--mdc-badge-padding-vertical) var(--mdc-badge-padding-horizontal);
			background-color: var(--mdc-badge-primary-background);
			transition: background-color var(--mdc-badge-transition-duration) var(--mdc-badge-transition-timing),
				color var(--mdc-badge-transition-duration) var(--mdc-badge-transition-timing);
			gap: var(--mdc-badge-gap);
		}

		/* breakpoints.md */
		@media (min-width: 768px) {
			.mdc-badge {
				font-size: var(--mdc-badge-font-size-desktop);
			}
		}

		.mdc-badge--style-primary {
			color: var(--mdc-badge-primary-color);
		}

		a.mdc-badge--style-primary:not(:disabled):not([aria-disabled='true']):hover,
		a.mdc-badge--style-primary:not(:disabled):not([aria-disabled='true']):focus-visible,
		button.mdc-badge--style-primary:not(:disabled):not([aria-disabled='true']):hover,
		button.mdc-badge--style-primary:not(:disabled):not([aria-disabled='true']):focus-visible {
			background-color: var(--mdc-badge-primary-background-hover);
		}

		a.mdc-badge--style-primary:disabled,
		a.mdc-badge--style-primary[aria-disabled='true'],
		button.mdc-badge--style-primary:disabled,
		button.mdc-badge--style-primary[aria-disabled='true'] {
			color: var(--mdc-badge-primary-color-disabled);
			background-color: var(--mdc-badge-primary-background-disabled);
		}

		.mdc-badge--style-inverse {
			color: var(--mdc-badge-inverse-color);
			border: 1px solid var(--mdc-badge-inverse-border);
			background-color: var(--mdc-badge-inverse-background);
			transition: border-color var(--mdc-badge-transition-duration) var(--mdc-badge-transition-timing),
				background-color var(--mdc-badge-transition-duration) var(--mdc-badge-transition-timing);
		}

		a.mdc-badge--style-inverse:not(:disabled):not([aria-disabled='true']):hover,
		a.mdc-badge--style-inverse:not(:disabled):not([aria-disabled='true']):focus-visible,
		button.mdc-badge--style-inverse:not(:disabled):not([aria-disabled='true']):hover,
		button.mdc-badge--style-inverse:not(:disabled):not([aria-disabled='true']):focus-visible {
			background-color: var(--mdc-badge-inverse-background-hover);
			border-color: var(--mdc-badge-inverse-border-hover);
		}

		a.mdc-badge--style-inverse:disabled,
		a.mdc-badge--style-inverse[aria-disabled='true'],
		button.mdc-badge--style-inverse:disabled,
		button.mdc-badge--style-inverse[aria-disabled='true'] {
			color: var(--mdc-badge-inverse-color-disabled);
			background-color: var(--mdc-badge-inverse-background-disabled);
			border-color: var(--mdc-badge-inverse-border-disabled);
		}

		.mdc-badge--style-green {
			color: var(--mdc-badge-green-color);
			background-color: var(--mdc-badge-green-background);
		}

		a.mdc-badge--style-green:not(:disabled):not([aria-disabled='true']):hover,
		a.mdc-badge--style-green:not(:disabled):not([aria-disabled='true']):focus-visible,
		button.mdc-badge--style-green:not(:disabled):not([aria-disabled='true']):hover,
		button.mdc-badge--style-green:not(:disabled):not([aria-disabled='true']):focus-visible {
			background-color: var(--mdc-badge-green-background-hover);
		}

		a.mdc-badge--style-green:disabled,
		a.mdc-badge--style-green[aria-disabled='true'],
		button.mdc-badge--style-green:disabled,
		button.mdc-badge--style-green[aria-disabled='true'] {
			color: var(--mdc-badge-green-color-disabled);
			background-color: var(--mdc-badge-green-background-disabled);
		}

		.mdc-badge--style-red {
			color: var(--mdc-badge-red-color);
			background-color: var(--mdc-badge-red-background);
		}

		a.mdc-badge--style-red:not(:disabled):not([aria-disabled='true']):hover,
		a.mdc-badge--style-red:not(:disabled):not([aria-disabled='true']):focus-visible,
		button.mdc-badge--style-red:not(:disabled):not([aria-disabled='true']):hover,
		button.mdc-badge--style-red:not(:disabled):not([aria-disabled='true']):focus-visible {
			background-color: var(--mdc-badge-red-background-hover);
		}

		a.mdc-badge--style-red:disabled,
		a.mdc-badge--style-red[aria-disabled='true'],
		button.mdc-badge--style-red:disabled,
		button.mdc-badge--style-red[aria-disabled='true'] {
			color: var(--mdc-badge-red-color-disabled);
			background-color: var(--mdc-badge-red-background-disabled);
		}

		.mdc-badge--style-blue {
			color: var(--mdc-badge-blue-color);
			background-color: var(--mdc-badge-blue-background);
		}

		a.mdc-badge--style-blue:not(:disabled):not([aria-disabled='true']):hover,
		a.mdc-badge--style-blue:not(:disabled):not([aria-disabled='true']):focus-visible,
		button.mdc-badge--style-blue:not(:disabled):not([aria-disabled='true']):hover,
		button.mdc-badge--style-blue:not(:disabled):not([aria-disabled='true']):focus-visible {
			background-color: var(--mdc-badge-blue-background-hover);
		}

		a.mdc-badge--style-blue:disabled,
		a.mdc-badge--style-blue[aria-disabled='true'],
		button.mdc-badge--style-blue:disabled,
		button.mdc-badge--style-blue[aria-disabled='true'] {
			color: var(--mdc-badge-blue-color-disabled);
			background-color: var(--mdc-badge-blue-background-disabled);
		}

		.mdc-badge--type-clickable-strong {
			text-decoration-line: underline;
			text-decoration-style: solid;
			text-decoration-skip-ink: auto;
			text-decoration-thickness: auto;
			text-underline-offset: auto;
			text-underline-position: from-font;
		}

		:host([size='sm']) {
			--mdc-badge-height: 1.25rem;
			--mdc-badge-font-size: 0.5625rem;
			--mdc-badge-font-size-desktop: 0.5625rem;
			--mdc-badge-padding-vertical: 0.125rem;
			--mdc-badge-padding-horizontal: 0.375rem;
			--mdc-badge-border-radius: 0.1875rem;
		}

		:host([size='lg']) {
			--mdc-badge-height: 2rem;
			--mdc-badge-font-size: 0.875rem;
			--mdc-badge-font-size-desktop: 0.875rem;
			--mdc-badge-padding-vertical: 0.375rem;
			--mdc-badge-padding-horizontal: 0.75rem;
			--mdc-badge-border-radius: 0.3125rem;
		}
	`

  render() {
    const className = `mdc-badge mdc-badge--style-${this.variant} mdc-badge--type-${this.type}`

    const content = html`<slot></slot>`

    if (this.isClickable && !this.href) {
      return html`<button type="button" class=${className} ?disabled=${this.disabled}>${content}</button>`
    }

    if (this.href) {
      return html`<a class=${className} href=${this.href} aria-disabled=${this.disabled ? 'true' : undefined}>${content}</a>`
    }

    return html`<span class=${className}>${content}</span>`
  }
}
